import {action, Action, computed, Computed, thunk, Thunk} from 'easy-peasy';
import {CommonModel, getCommonModel, fetchCancel} from 'store/commonStore';
import { projectRemote } from '~/data/remote/ProjectRemoteDataSource';
import {API} from 'app/routes';
import {StoreModel} from 'store/index';
import {Project} from 'types/index';
import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { projectRepository } from '~/data/repositories';
import {
    canAccessProjectOffline,
    setFavoriteProjectIds,
    setLastUsedProjectIds,
} from '~/sqlite/lastUsedCache';

interface ProjectsFavoritePayload {
    projectId: Project['id'],
    favorite: boolean
}

interface ProjectsLastUsedPayload {
    projectId: Project['id']
}

export interface ProjectsModel extends CommonModel<Project> {
    filteredList: Computed<ProjectsModel, Project[], StoreModel>

    lastUsed: Project[]
    fetchingLastUsed: boolean
    setFetchingLastUsed: Action<ProjectsModel, boolean>
    fetchLastUsed: Thunk<ProjectsModel, void, any, StoreModel, Promise<Project[]>>
    fetchLastUsedCancel: Action<ProjectsModel>
    fetchedLastUsed: Action<ProjectsModel, Project[]>

    inFavorite: Computed<ProjectsModel, (id: Project['id']) => boolean, StoreModel>
    isOfflineAvailable: Computed<ProjectsModel, (id: Project['id']) => boolean, StoreModel>
    favorites: Project[]
    fetchingFavorites: boolean
    setFetchingFavorites: Action<ProjectsModel, boolean>
    fetchFavorites: Thunk<ProjectsModel, void, any, StoreModel, Promise<Project[]>>
    fetchFavoritesCancel: Action<ProjectsModel>
    fetchedFavorites: Action<ProjectsModel, Project[]>

    favoritesUpdating: boolean
    setFavoritesUpdating: Action<ProjectsModel, boolean>
    updateFavorites: Thunk<ProjectsModel, ProjectsFavoritePayload, any, StoreModel, Promise<null>>
    updatedFavorites: Action<ProjectsModel, Project[]>

    updateLastUsed: Thunk<ProjectsModel, ProjectsLastUsedPayload, any, StoreModel, Promise<null>>

    setOfflineFavorites: Thunk<ProjectsModel, Project[], any, StoreModel, Promise<void>>
    cacheOpenedProject: Thunk<ProjectsModel, Project['id'], any, StoreModel, Promise<void>>
}

export const projectsModel: ProjectsModel = {
    ...getCommonModel<Project>(API.projects),

    filteredList: computed(state => state.list.filter(item => (
        !state.lastUsed.find(({id}) => item.id === id) &&
        !state.favorites.find(({id}) => item.id === id)
    ))),

    lastUsed: [],
    fetchingLastUsed: false,
    setFetchingLastUsed: action((state, payload) => {
        state.fetchingLastUsed = payload;
    }),
    fetchLastUsed: thunk(async (actions) => {
        actions.setFetchingLastUsed(true);
        try {
            if (connectivityService.isOnline()) {
                const data = await projectRemote.fetchLastUsed();
                setLastUsedProjectIds(data.map(project => project.id));
                actions.fetchedLastUsed(data);
                return data;
            }
            const rows = await projectRepository.getOfflineLastUsed();
            actions.fetchedLastUsed(rows);
            return rows;
        } finally {
            actions.setFetchingLastUsed(false);
        }
    }),
    fetchLastUsedCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedLastUsed: action((state, payload) => {
        state.lastUsed = payload;
    }),

    inFavorite: computed(
        state => (id: Project['id']): boolean => !!state.favorites.find(item => item.id.toString() === id.toString())
    ),
    isOfflineAvailable: computed(
        state => (id: Project['id']): boolean =>
            canAccessProjectOffline(
                id,
                !!state.favorites.find(item => item.id.toString() === id.toString())
            ) || !!state.lastUsed.find(item => item.id.toString() === id.toString())
    ),
    favorites: [],
    fetchingFavorites: false,
    setFetchingFavorites: action((state, payload) => {
        state.fetchingFavorites = payload;
    }),
    fetchFavorites: thunk(async (actions) => {
        actions.setFetchingFavorites(true);
        try {
            if (connectivityService.isOnline()) {
                const data = await projectRemote.fetchFavorites();
                actions.fetchedFavorites(data);
                await actions.setOfflineFavorites(data);
                return data;
            }
            const rows = await projectRepository.getOfflineFavorites();
            actions.updatedFavorites(rows);
            return rows;
        } finally {
            actions.setFetchingFavorites(false);
        }
    }),
    fetchFavoritesCancel: action(() => {
        fetchCancel('Operation canceled by the user');
    }),
    fetchedFavorites: action((state, payload) => {
        state.favorites = payload;
    }),

    favoritesUpdating: false,
    setFavoritesUpdating: action((state, payload) => {
        state.favoritesUpdating = payload;
    }),
    updateFavorites: thunk(async (actions, payload) => {
        actions.setFavoritesUpdating(true);
        try {
            const data = await projectRemote.updateFavorites(payload);
            const list = await projectRemote.fetchFavorites();
            actions.updatedFavorites(list);
            await actions.setOfflineFavorites(list);
            return data;
        } finally {
            actions.setFavoritesUpdating(false);
        }
    }),
    updatedFavorites: action((state, payload) => {
        state.favorites = payload;
    }),

    updateLastUsed: thunk((actions, payload) => {
        return projectRemote.updateLastUsed(payload);
    }),

    setOfflineFavorites: thunk(async (actions, payload) => {
        setFavoriteProjectIds(payload.map(project => project.id));
        try {
            const lastUsed = await projectRemote.fetchLastUsed();
            setLastUsedProjectIds(lastUsed.map(project => project.id));
            actions.fetchedLastUsed(lastUsed);
        } catch {
            // Keep previous server snapshot on fetch failure so offline guards still work.
        }

        await projectRepository.syncFavoritesCache(payload);
    }),

    cacheOpenedProject: thunk(async (_actions, id, { getStoreState }) => {
        const project = getStoreState().project.data;
        if (!project || String(project.id) !== String(id)) {
            return;
        }
        await projectRepository.cacheOpenedProject(project);
    }),
};
